import * as React from 'react';
import { useContext } from 'react';
import { Redirect } from 'react-router';
import { FilterableObject } from 'modules/common/hooks/useFiltered';
import useSetDocTitle from 'modules/common/hooks/useSetDocTitle';
import { checkConfidence } from 'modules/common/util/Confidence';
import AuthenticationContext from '../../auth/AuthenticationContext';
import { getAuthenticationRedirectRoute } from '../../auth/authenticationRedirectHandler';
import { SessionContext } from '../../context/SessionContext';
import { Error403Trigger, Error404Trigger } from './ErrorPageProvider';

export interface RouteGuardProps extends Pick<FilterableObject, 'confidence' | 'globalPermission' | 'hidden'> {
  /** Page title to set when the guarded content renders */
  title?: string;
  /**
   * If true, renders 403 when the user is authenticated but not authorized. Evaluated after the login-redirect
   * check. Used for org permission failures.
   */
  notAuthorized?: boolean;
  /** If true, allows unauthenticated access. Default: false (requires auth, redirects to login if not signed in). */
  isPublic?: boolean;
}

/**
 * Wraps route content with the Developer Site access rules. The checks run in this order, and the first
 * one that applies decides the result:
 *
 * 1. Not signed in and not `isPublic` → redirect to login
 * 2. `notAuthorized`, or missing `globalPermission` → 403
 * 3. `hidden`, or `confidence` not met → 404
 * 4. Otherwise render the children and set the page title
 *
 * @example
 * <Route path={routes.console} render={() => <RouteGuard title='Console'><ConsoleApp /></RouteGuard>} />
 */
export const RouteGuard: React.FC<React.PropsWithChildren<RouteGuardProps>> = ({
  confidence,
  globalPermission,
  hidden,
  notAuthorized,
  isPublic = false,
  title,
  children,
}) => {
  const sessionContext = useContext(SessionContext);
  const authContext = useContext(AuthenticationContext);

  if (!isPublic && !sessionContext?.isSignedIn()) {
    const redirectTo = getAuthenticationRedirectRoute(401, sessionContext?.info);
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    return <Redirect to={redirectTo} />;
  }

  const isUserAuthorized = !notAuthorized && (!globalPermission || authContext.hasGlobalPermission(globalPermission));
  if (!isUserAuthorized) {
    return <Error403Trigger />;
  }

  if (hidden || (confidence && !checkConfidence(confidence))) {
    return <Error404Trigger />;
  }

  // PageTitle follows the children so on mount its effect runs last and the route title wins over a child's own title
  return (
    <>
      {children}
      <PageTitle title={title} />
    </>
  );
};

const PageTitle: React.FC<{ title?: string }> = ({ title }) => {
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  useSetDocTitle(title);
  return null;
};

export default RouteGuard;
